import {P, match} from 'ts-pattern'
import {BinForecast, BinWrapProps} from '../../chart'
import {SubqType, subqTypes, Subqs, QuestionCfgCommon, ForecastQuestionCfgAmount} from 'shared/forecast'
import {compactString} from 'shared/text'
import {mapEntries, notNullOrEmpty} from 'shared/collection'
import {doSafe} from 'shared/promise'
import {hashStringToNumber} from 'shared/text'
import {StyleProps, useMemoValue, useElementSize, ChildrenProps, WrapIf, Markdown} from 'shared/ui'
import {ForecastQuestionCfg, ResolutionForecasts} from '../forecast'
import {getResultsQuestion, QuestionColor, QuestionResultsQuestion, ResolutionValue} from '../question'
import {mapToObj, pick, omit, values, flatMap, partition, isEmpty, groupBy, uniqueBy, isNonNullish, pipe} from 'remeda'
import {useMemo, useEffect, useState, ReactNode} from 'react'
import {MantineTheme, MantineThemeColors, useSafeMantineTheme} from '@mantine/core'
import {useScrollIntoView} from '@mantine/hooks'
import {LabelEmptyList, LabelType} from '../../ui/Labels'
import {QRLikelihood} from './QRLikelihood'
import {QRAmount} from './QRAmount'
import {z} from 'zod'
import {queryStringSerializer} from '../../queryStringSerializer'
import {QuestionResultsLayout as QR, QRComparison} from './questionResultsLayout'
import {SErrorBoundary} from '../../ui/SErrorBoundary'
import {twMerge} from 'tailwind-merge'
import {Tip, useTip} from '../../ui/Tip'
import React from 'react'

export type ForecastChartItem<T extends ForecastQuestionCfg = ForecastQuestionCfg> = Pick<
  ResolutionForecasts,
  'forecasts' | 'resolution' | 'group' | 'shouldVizRelative' | 'baselineAgg' | 'questionAgg' | 'code' | 'resAgg'
> & {
  labelLong?: string
  cfg: T
}
export type ForecastChartBaseProps<T extends BinForecast = BinForecast> = {
  color?: string
  /** runtime configuration */
  cfg: Pick<QuestionResultsQuestion, 'colors' | 'chart' | 'aggVersion'>
  parentCfg?: Pick<QuestionCfgCommon, 'title'>
  showHelp?: boolean
  showScale?: boolean
  hideBaselineLabel?: boolean
  hideAxisLabels?: boolean
  tag?: ReactNode
  width?: number
  comparisons?: QRComparison[]
} & Pick<BinWrapProps<T>, 'noteCustom'>

export type ForecastChartProps<
  T extends BinForecast = BinForecast,
  TQ extends ForecastQuestionCfg = ForecastQuestionCfg
> = ForecastChartItem<TQ> & ForecastChartBaseProps<T>
export type MultiForecastChartProps<T extends BinForecast = BinForecast> = {
  items: ForecastChartItem[]
} & ForecastChartBaseProps<T>

type ResultsColorProps = {
  colors: MantineThemeColors
  cfg?: Partial<Record<QuestionColor, string>>
  type: QuestionColor
}

/** shared functionality for the query on question results used by both embed api and the app */
export namespace ResultsQuery {
  export const showItems = ['question', 'cause', 'consequence', 'related', 'help', 'hideTitle', 'hideHelp'] as const
  export const showItemsCurrent = ['hideHelp', 'hideTitle'] as const
  export type ResultShowType = (typeof showItems)[number]
  export const zShow = z.array(z.enum(showItems))

  export const zQueryShape = {
    showQuestions: z.array(z.string()).optional(),
    show: zShow.optional(),
    r2_url: z.string().optional(),
    compare_ts: z.array(z.string()).optional(),
    forecasters: z.array(z.string()).optional()
  }

  export const zQuery = z.preprocess(
    q =>
      match(q)
        .with({conditionals: P.array(P.string)}, ({conditionals, ...props}) => ({
          ...props,
          showQuestions: conditionals
        }))
        .otherwise(() => q),
    z.object(zQueryShape)
  )

  export const zQueryNoUpgrade = z.object(zQueryShape)
  export type ResultsQuery = z.infer<typeof zQuery>
  export const serializer = queryStringSerializer(zQuery)
}

export const resultColorStatic = ({colors, cfg, type}: ResultsColorProps) =>
  cfg?.[type] ??
  match(type)
    .with('question', () => colors.amber?.[6])
    .with('group', () => colors.indigo?.[6])
    .with('true', () => '#53786b') // i wanted something close to red/green but works for colour blind people https://github.com/oftheheadland/Colorblindly
    .with('false', () => '#865687')
    .otherwise(() => undefined) ??
  colors.blue?.[6]

export const resultColorFn = (props: Omit<ResultsColorProps, 'type'>) => (type: QuestionColor) =>
  resultColorStatic({...props, type})

export const resolutionColor = (
  f: Pick<ResolutionForecasts, 'resolution' | 'cfg' | 'code'>,
  colors: MantineTheme['colors']
) => {
  const resColor =
    f.resolution.color ?
      (_: QuestionColor) => f.resolution.color!
    : resultColorFn({
        colors,
        cfg: f.cfg.colors
      })
  return resColor(
    match(f.code)
      .with('likelihood', () => 'true' as const)
      .with(P.string.endsWith('-true'), () => 'true' as const)
      .with(P.string.endsWith('-false'), () => 'false' as const)
      .otherwise(() => 'default')
  )
}

export type ResultColorFn = (type: QuestionColor) => string

export const useQuestionResultsQuestion = <T extends QuestionResultsQuestion | undefined>(
  question?: T
): T extends undefined ? undefined : QuestionResultsQuestion =>
  useMemoValue(question ? getResultsQuestion(question as any) : undefined) as any

export type QuestionResultsProps<T extends BinForecast = BinForecast> = QR.Props & {
  theme?: MantineTheme
  className?: string
  forecastsFetching?: boolean
} & Pick<ForecastChartBaseProps<T>, 'noteCustom'>

const useQuestionResults = <T extends BinForecast = BinForecast>(props: QuestionResultsProps<T>) => {
  const question = useQuestionResultsQuestion(props.question)
  const {colors} = useSafeMantineTheme()
  const resultsColor = resultColorFn({colors, cfg: question.colors})
  const {subqs} = props
  return useMemo(() => {
    const {groups, timestamps, shouldShow} =
      subqs ?
        QR.layout({...props, question, resultsColor})
      : {groups: undefined, timestamps: [], shouldShow: () => false}
    const showTsTags =
      timestamps.length > 1 ? 'multi'
      : timestamps.length == 1 && timestamps[0]!.ts != null ? 'custom'
      : undefined
    return {question, shouldShow, groups, timestamps, showTsTags} as const
  }, [
    question,
    subqs,
    props.show?.join('|'),
    props.showQuestions?.join('|'),
    props.subquestionHighlightId,
    props.getForecasts,
    props.timestamps,
    props.forecasters?.join('|')
  ])
}

type UseQuestionResults = ReturnType<typeof useQuestionResults>

export const QuestionResults = (props: QuestionResultsProps) => {
  const useQr = useQuestionResults(props)
  const {question, shouldShow, groups, showTsTags} = useQr
  const {width: containerWidth, ref: containerRef} = useElementSize()
  // // if the question is above sub-q's, then limit its size, otherwise fill the size of container
  const questionWidth =
    containerWidth == null ? undefined
    : shouldShow('cause') || shouldShow('consequence') || shouldShow('related') ? Math.min(containerWidth, 600)
    : containerWidth

  const showHelp = shouldShow('hideHelp') ? [] : [true]
  const subqTypesToShow = subqTypes.filter(shouldShow)

  const showNoForecastsYet = [!props.forecastsFetching]

  return (
    <div ref={containerRef} id='question-results' className={twMerge('@container space-y-[6em]', props.className)}>
      {groups?.map(g => {
        const anyDisplayed =
          [g.groupResolution, ...g.subqResolutions.map(t => t.tsResolutions)].filter(s =>
            s?.some(r => !isEmpty(r.resolutions) && r.resolutions.some(r => !isEmpty(r.forecasts)))
          ).length > 0
        const subqsByType = groupBy(
          g.subqResolutions.filter(
            r =>
              r.tsResolutions.some(r => r.resolutions.some(r => !isEmpty(r.forecasts))) &&
              subqTypesToShow.includes(r.cfg.subquestionType)
          ),
          s => s.cfg.subquestionType
        )
        const showGroup =
          anyDisplayed &&
          (g.isRootQuestion ? !g.cfg.disableQuestionForecast && !!shouldShow('question') : true) &&
          !g.hideGroupQuestion

        const commonChartProps =
          questionWidth ?
            ({
              ...pick(props, ['noteCustom']),
              // width: questionWidth,
              cfg: {...pick(question, ['colors', 'chart', 'aggVersion'])},
              parentCfg: {title: g.cfg.title}
            } satisfies Partial<ForecastChartBaseProps>)
          : undefined

        return (
          <div key={`group-${g.id}`} className='flex flex-col gap-4'>
            {anyDisplayed ?
              <>
                {showGroup && commonChartProps != null && (
                  <div
                    className='mx-auto w-full space-y-2'
                    // if the question is above conditionals, then limit its size, otherwise fill the size given
                    style={shouldShow('cause') || shouldShow('consequence') ? {maxWidth: '50em'} : {}}>
                    {!shouldShow('hideTitle') && showGroup && (
                      <QRTitle
                        title={g.cfg.title}
                        subTitle={g.cfg.subTitle}
                        hideSubTitles={question.hideSubTitles}
                        headingElement='h2'
                        className='text-center'
                      />
                    )}
                    {match(g.cfg.forecastType)
                      .with('likelihood', () => {
                        const showScale = [true]
                        return g.groupResolution
                          .filter(r => r.resolutions[0]?.resolution.enableForecasts)
                          .map(r => {
                            const rFirst = r.resolutions[0]
                            return (
                              <RowForRes key={`group-likelihood-${r.key}`} resolutionValue={rFirst?.resolutionValue}>
                                <QRLikelihood
                                  className='grow'
                                  {...commonChartProps}
                                  items={r.resolutions}
                                  mode='text'
                                  showHelp={showHelp.pop()}
                                  showScale={showScale.pop()}
                                  tag={showTsTags ? <TsTag tsName={r.tsName} /> : undefined}
                                  debugTitle={r.tsName}
                                  color={rFirst?.resolution.color}
                                  comparisons={rFirst?.comparisons}
                                />
                              </RowForRes>
                            )
                          })
                      })
                      .with('amount', () =>
                        g.groupResolution.map(tr =>
                          tr.resolutions.map(r =>
                            match(r)
                              .with({cfg: {forecastType: 'amount'}}, r => (
                                <RowForRes
                                  key={`group-amount-${tr.key}|${r.code}`}
                                  resolutionValue={r.resolutionValue}
                                  fmt={r.fmt}>
                                  <QRAmount
                                    {...commonChartProps}
                                    {...r}
                                    showHelp={showHelp.pop()}
                                    tag={showTsTags ? <TsTag tsName={tr.tsName} /> : undefined}
                                  />
                                </RowForRes>
                              ))
                              .otherwise(() => undefined)
                          )
                        )
                      )
                      .exhaustive()}
                  </div>
                )}

                {!isEmpty(subqsByType) && (
                  <div
                    className='gap-8 grid justify-center'
                    style={{
                      gridTemplateColumns: `repeat(auto-fit, minmax(min(35em, 100%), 1fr))`
                    }}>
                    {mapEntries(subqsByType, (t, sqs) => (
                      <div key={`sqlist-${t}`} className='flex justify-center'>
                        <RList
                          {...{
                            className: 'flex-1 max-w-[60em]',
                            ...pick(props, ['noteCustom']),
                            // show labels above the lists when there are two of them
                            showTypeLabel: values(subqsByType).length > 1,
                            hideTitle: shouldShow('hideTitle'),
                            showTsTags
                          }}
                          question={question}
                          parentResolutions={g.groupResolution}
                          subquestions={sqs!}
                          subquestionType={t}
                          showHelp={showHelp.pop()}
                          className='max-w-[60em] w-full'
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            : showNoForecastsYet.pop() && <QRNoForecastsYet />}
          </div>
        )
      })}
    </div>
  )
}

const QRTitle = ({
  title,
  subTitle,
  hideSubTitles = false,
  headingElement: HeadingTag = 'h2',
  className
}: {
  title: ReactNode
  subTitle?: ReactNode
  hideSubTitles?: boolean
  headingElement?: 'h1' | 'h2' | 'h3' | 'h4'
  className?: string
}) => (
  <div className={twMerge('text-balance leading-tight', className)}>
    <HeadingTag className=''>{title}</HeadingTag>
    {!hideSubTitles && notNullOrEmpty(subTitle) && <div className='opacity-60 text-sm leading-tight'>{subTitle}</div>}
  </div>
)

export const QRNoForecastsYet = () => <LabelEmptyList className='mt-10'>no forecasts yet...</LabelEmptyList>

const TsTag = ({tsName, className, style}: Pick<QR.TsMd, 'tsName'> & StyleProps) => (
  <div
    className={`px-3 py-1 rounded-lg text-sm bg-gray-600 text-white font-semibold text-nowrap ?? ${className}`}
    style={style}>
    <span className='opacity-60 font-normal text-xs'>forecasts as of </span> {tsName}
  </div>
)

type SubqResultsProps = {
  subquestionType: SubqType
  subquestions: Array<QR.LayoutSubqResolution>
  question: QuestionResultsQuestion
  parentQuestion?: Subqs.GroupParentQuestionCfg
  parentResolutions?: QR.LayoutGroupResolution[]
  showEachForecast?: boolean
  showTypeLabel?: boolean
  showHelp?: boolean
  hideTitle?: boolean
} & Pick<UseQuestionResults, 'showTsTags'> &
  Pick<QuestionResultsProps, 'noteCustom'> &
  StyleProps

const RList = ({
  subquestionType,
  subquestions: subquestionsRaw,
  question,
  className,
  showTypeLabel,
  showHelp: _showHelp,
  hideTitle,
  parentQuestion,
  parentResolutions,
  noteCustom,
  showTsTags
}: SubqResultsProps) => {
  const {ref, width} = useElementSize()

  const subquestions = subquestionsRaw
    .map(s => ({...s.cfg, ...omit(s, ['cfg'])}))
    .filter(s => s.tsResolutions.some(rs => rs.resolutions.length > 0))

  const showHelp = _showHelp ? [true] : []
  const firstResolutionWithQuestionAgg = [true]
  const resolutionId = (s: {id: string; subquestionType: SubqType; code?: string}) =>
    `${s.subquestionType == 'consequence' ? '' : s.id}|${s.code}`

  const showLabelByResolution = mapToObj(
    uniqueBy(
      flatMap(subquestions, s =>
        (s.tsResolutions ?? [])
          .flatMap(rs => rs.resolutions)
          .map(f => ({resId: resolutionId({...pick(s, ['id', 'subquestionType']), code: f.code})}))
      ),
      r => r.resId
    ),
    r => [r.resId, [true]]
  )

  const {targetRef: scrollRef} = useScrollIntoView<HTMLDivElement>({})
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (scrollRef.current && !scrolled) {
      scrollRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
      setScrolled(true)
    }
  }, [scrollRef.current])

  return (
    <div ref={ref} className={`${className ?? ''}`}>
      {width > 0 && (
        <>
          {showTypeLabel && (
            <LabelType>
              {match(subquestionType)
                .with('cause', 'consequence', t => `${t}s`)
                .with('related', () => 'related')
                .exhaustive()}
            </LabelType>
          )}
          <div className='flex flex-col gap-[1.5em]'>
            {subquestions.map(sq => {
              // for each consequence group by the unit label. If they are the same then they should have the same bounds?
              if (subquestions.length == 0) return <></>
              const firstByType = {
                likelihood: [true],
                amount: [true]
              }
              const allResolutions = sq.tsResolutions.flatMap(rs => rs.resolutions)
              const showTitle = [true]
              const firstResolutionWithBaseline = [true]
              const shownAxisLabels = new Set<string>()
              const shouldHideAxisLabels = (cfg: Pick<ForecastQuestionCfgAmount, 'defaultBounds' | 'unitLabel'>) => {
                const key = JSON.stringify(pick(cfg, ['defaultBounds', 'unitLabel']))
                const res = shownAxisLabels.has(key)
                if (!res) shownAxisLabels.add(key)
                return res
              }

              const {colors, chart, aggVersion} = question
              const common = {
                cfg: {colors, chart, aggVersion},
                parentCfg: parentQuestion ? {title: parentQuestion?.title} : undefined,
                noteCustom
              } satisfies Partial<ForecastChartBaseProps>

              return sq.tsResolutions.map(({resolutions, ts, tsName}) => {
                // if any forecast should have a label, then we show them for all in this subquestion
                const anyShowLabel = allResolutions
                  .map(r =>
                    showLabelByResolution[resolutionId({...pick(sq, ['id', 'subquestionType']), code: r.code})]?.pop()
                  )
                  .some(v => v)

                const rfAll = resolutions.map(r => ({
                  ...r,
                  resolution: {
                    ...r.resolution,
                    label:
                      anyShowLabel ?
                        r.resolution.label
                      : (compactString(r.resolution.labelShort) ?? r.resolution.label),
                    labelLong: r.resolution.label
                  },
                  isFirstAgg:
                    subquestionType == 'cause' && r?.questionAgg ? firstResolutionWithQuestionAgg.pop() == true
                    : subquestionType == 'consequence' && r?.baselineAgg ? firstResolutionWithBaseline.pop() == true
                    : false,
                  isFirstByType: firstByType[r.cfg.forecastType].pop() == true
                }))

                const noForecasts = rfAll.every(r => isEmpty(r.forecasts))
                const id = sq.id

                const combinedLikelihoods = pipe(
                  rfAll.filter(
                    f =>
                      question.chart.relative == true &&
                      f.cfg.forecastType == 'likelihood' &&
                      f.code?.match(/-(true|false)$/) != null &&
                      (f.questionAgg?.group != null || f.baselineAgg?.group != null) &&
                      (f.resolutionValue == null || f.resolutionValue.status == 'pending') && // show relative only if there isn't a resolution value
                      f.resAgg != null &&
                      f.baselineAgg != null &&
                      f.baselineAgg.group != null
                  ),
                  cs => {
                    if (cs.length != 2) return []
                    const a = cs[0]!
                    const b = cs[1]!
                    const baseline = a.baselineAgg!.group!
                    const relativeA = a.resAgg! - baseline
                    const relativeB = b.resAgg! - baseline
                    const sameSign = (relativeA < 0 && relativeB < 0) || (relativeA > 0 && relativeB > 0)
                    const baselineCloseToEnds = baseline < 15 || baseline > 85
                    // if these things are the same sign, then put them back into standalone resolutions
                    if (sameSign || baselineCloseToEnds) return []
                    return [a, b]
                  }
                )

                const finalItems = rfAll
                  .map(r =>
                    combinedLikelihoods.includes(r) ?
                      combinedLikelihoods[0] == r ?
                        {type: 'combined-likelihoods' as const, items: combinedLikelihoods}
                      : undefined
                    : {type: 'standalone' as const, item: r}
                  )
                  .filter(isNonNullish)

                const showTitleCurrent = !hideTitle && showTitle.pop()
                const showTag = [showTsTags ? true : false]
                return (
                  <div
                    key={`sq-${id}|${ts}`}
                    ref={sq.highlighted ? scrollRef : undefined}
                    className={`${sq.highlighted ? 'rounded-lg bg-primary/5 px-2' : ''} `}>
                    {!hideTitle && (
                      <div
                        className='flex w-full items-end'
                        style={showTsTags == 'multi' && showTitleCurrent ? {paddingTop: '2em'} : {}}>
                        <QRTitle
                          title={sq.title}
                          subTitle={sq.subTitle}
                          hideSubTitles={question.hideSubTitles}
                          headingElement='h3'
                          className='my-[0.3em]'
                        />
                        {showTag.pop() ?
                          <TsTag className='ml-auto' tsName={tsName} />
                        : null}
                      </div>
                    )}
                    <div className='flex flex-col mt-1 gap-[1em]'>
                      {noForecasts && (
                        <div className='flex justify-between'>
                          <QRNoForecastsYet />
                          {showTag.pop() ?
                            <TsTag tsName={tsName} />
                          : null}
                        </div>
                      )}
                      {finalItems.map(item => {
                        return match(item)
                          .with({type: 'combined-likelihoods'}, ({items}) => {
                            const a = items[0]!
                            return (
                              <RowForRes key={`combined-${a.code}`} resolutionValue={a.resolutionValue}>
                                <QRLikelihood
                                  items={items}
                                  {...common}
                                  showHelp={showHelp.pop()}
                                  mode='bar'
                                  hideBaselineLabel={!a.isFirstAgg}
                                  showScale={a.isFirstByType}
                                  debugTitle='relative'
                                  tag={showTag.pop() ? <TsTag tsName={tsName} /> : null}
                                  className='grow'
                                  comparisons={items.flatMap(r => r.comparisons ?? [])}
                                />
                              </RowForRes>
                            )
                          })
                          .with({type: 'standalone'}, ({item: f}) => {
                            const fCommon = {
                              ...common,
                              cfg: {...common.cfg, title: sq.title},
                              resolution: {
                                ...f.resolution,
                                label:
                                  anyShowLabel ?
                                    f.resolution.label
                                  : (compactString(f.resolution.labelShort) ?? f.resolution.label)
                              },

                              showHelp: showHelp.pop(),
                              tag: showTag.pop() ? <TsTag tsName={tsName} /> : null,
                              comparisons: f.comparisons
                            }

                            const parentRes = parentResolutions
                              ?.find(r => r.ts == ts)
                              ?.resolutions.find(r => r.code == undefined)

                            return (
                              <RowForRes key={`sq-res-${f.code}`} resolutionValue={f.resolutionValue} fmt={f.fmt}>
                                {match(f)
                                  .with({cfg: {forecastType: 'likelihood'}}, f => (
                                    <QRLikelihood
                                      items={[f]}
                                      {...fCommon}
                                      mode='bar'
                                      hideBaselineLabel={!f.isFirstAgg}
                                      showScale={f.isFirstByType}
                                      className='grow'
                                    />
                                  ))
                                  .with({cfg: {forecastType: 'amount'}}, f => (
                                    <QRAmount
                                      {...fCommon}
                                      {...f}
                                      hideAxisLabels={shouldHideAxisLabels(f.cfg)}
                                      baselineForecasts={f.questionAgg ? parentRes : undefined}
                                    />
                                  ))
                                  .exhaustive()}
                              </RowForRes>
                            )
                          })
                          .exhaustive()
                      })}
                    </div>
                  </div>
                )
              })
            })}
          </div>
        </>
      )}
    </div>
  )
}

const RowForRes = ({
  children,
  resolutionValue,
  fmt
}: ChildrenProps & {resolutionValue?: ResolutionValue; fmt?: (n: number) => string}) => (
  <SErrorBoundary>
    {resolutionValue == null || resolutionValue.status == 'pending' ?
      children
    : <div
        className='w-full flex gap-2 items-center'
        style={{flexDirection: resolutionValue.status == 'false' ? 'row-reverse' : 'row'}}>
        <WrapIf
          if={resolutionValue?.status == 'n/a'}
          wrap={c => <div className='grayscale-[60%] grow w-full opacity-60'>{c}</div>}>
          {children}
        </WrapIf>
        <ResolutionValueIcon {...{...resolutionValue, fmt}} />
      </div>
    }
  </SErrorBoundary>
)

export const ResolutionValueIcon = ({
  status,
  note,
  /** use this both for custom labels and formatted value's */
  label: labelRaw,
  num,
  className,
  fmt
}: ResolutionValue & {fmt?: (n: number) => string; className?: string}) => {
  const [deg] = useState(hashStringToNumber(`${status}${num}${labelRaw}`, 12) - 6)
  const color = match(status)
    .with('true', () => 'var(--mantine-color-green-6)')
    .with('false', 'value', () => 'var(--mantine-color-red-6)')
    .otherwise(() => 'var(--mantine-color-gray-6)')
  const label =
    status == 'value' ? doSafe(num, n => (fmt ? fmt(n) : n.toString()))
    : isEmpty(labelRaw) ? status
    : labelRaw
  const tipStore = useTip()
  return match(status)
    .with('true', 'false', 'n/a', 'value', () => (
      <WrapIf
        if={!isEmpty(note)}
        wrap={c => (
          <Tip store={tipStore} offset={-10} label={<Markdown>{note}</Markdown>}>
            <div className='shrink'>{c}</div>
          </Tip>
        )}>
        <div className='p-2 cursor-default'>
          <div
            className={twMerge('px-[0px]', className)}
            style={{
              transform: `rotate(${deg}deg)`
            }}>
            <div
              className='pb-1 rounded-md shadow-md border-2'
              style={{
                backgroundColor: `color-mix(in srgb, ${color}, transparent 30%)`,
                borderColor: color
              }}>
              <div className='text-white/70 leading-tight text-sm px-2 font-bold'>resolved</div>
              <div
                className='mx-[-6px] px-3 py-[2px] rounded-md text-white/90 text-sm font-extrabold uppercase text-center'
                style={{backgroundColor: color}}>
                {label}
              </div>
            </div>
          </div>
        </div>
      </WrapIf>
    ))
    .otherwise(() => <></>)
}
